/* Strike Gently sg-aov.js — add-ons to theme.js, loaded right after it.
   1. Promo-bar milestones: bars with a second/third step on the same ladder (m2_at / m3_at on a progress block,
      e.g. blankets 2 → 3) are kept live after cart changes with the same rules as snippets/promo-bar.liquid.
   2. Bonus-item rules that fire on the cart total (a bonus-items rule with trigger "total", e.g. a free pack at
      $75+). None configured right now — the machinery stays so it can be switched on from the editor.
      Safety: a reward that is not free in the cart (its discount missing or inactive) is removed again.
   3. One-tap add-on lines in the drawer (pin club, wall clips): adds with its selling plan, applies the code.
      3b. The drawer's "pairs with" cards add on a single tap.
   4. Express checkout buttons in the drawer survive the drawer's re-render.
   Add ?sgdebug to the URL to log swallowed errors. */
(function () {
  'use strict';
  const SG = window.SG;
  if (!SG || !SG.cart || !SG.drawer) return;
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const warn = (...a) => { if (SG.debug && window.console) console.warn('[SG aov]', ...a); };
  const money = SG.formatMoney || ((c) => '$' + ((Number(c) || 0) / 100).toFixed(2));
  const excluded = new Set((SG.promoExclude || []).map((h) => String(h).trim()).filter(Boolean));
  const isBonus = (it) => !!(it.properties && it.properties._bonus);
  const counts = (it) => !isBonus(it) && !excluded.has(it.handle);
  const typeCount = (cart, type) => cart.items.reduce((n, it) => (counts(it) && it.product_type === type ? n + it.quantity : n), 0);

  /* ---------- 1. Milestones ---------- */
  function refreshMilestones(cart) {
    if (!cart) return;
    $$('[data-promo][data-m2-at]').forEach((bar) => {
      const isCart = bar.dataset.targetType === 'cart';
      const mult = isCart ? 100 : 1;
      const goal = (Number(bar.dataset.threshold) || 0) * mult;
      const m2 = (Number(bar.dataset.m2At) || 0) * mult;
      const m3 = (Number(bar.dataset.m3At) || 0) * mult;
      const count = isCart ? cart.total_price : typeCount(cart, bar.dataset.target);
      if (!(count > 0 && goal > 0 && count >= goal && m2 > goal)) return; // below the first step: theme.js's text stands
      const req = bar.dataset.requiresType;
      if (req && typeCount(cart, req) === 0) return;
      let state, remaining = 0, text, from = 0, to = 0;
      if (count < m2) { state = 'progressing'; remaining = m2 - count; text = bar.dataset.m2Before; from = goal; to = m2; }
      else if (m3 > m2 && count < m3) { state = 'progressing'; remaining = m3 - count; text = bar.dataset.m3Before; from = m2; to = m3; }
      else { state = 'completed'; text = m3 > m2 ? bar.dataset.m3After : bar.dataset.m2After; }
      const won = !isCart && goal > 0 ? Math.floor(count / goal) : 0;
      const freeCount = won * (Number(bar.dataset.rewardPerTier) || 0);
      const plural = remaining === 1 ? '' : 's';
      text = (text || '')
        .replace(/\{moneyFrom\}/g, money(Math.max(0, remaining)))
        .replace(/\{cartTotal\}/g, money(cart.total_price))
        .replace(/\{from\}/g, String(Math.max(0, remaining)))
        .replace(/\{s\?\}/g, plural)
        .replace(/\{freeCount\}/g, String(freeCount))
        .replace(/\{tierThreshold\}/g, bar.dataset.threshold || '');
      // item steps count the step just reached as progress (2 of 2→3 shows half full, not empty); dollar steps refill from 0
      const pct = state === 'completed' ? 100 : isCart ? Math.round(((count - from) / Math.max(1, to - from)) * 100) : Math.round(((count - from + 1) / Math.max(1, to - from + 1)) * 100);
      bar.style.setProperty('--pct', Math.max(0, Math.min(100, pct)) + '%');
      bar.dataset.state = state;
      bar.hidden = false;
      const t = $('[data-promo-text]', bar);
      if (t && t.innerHTML !== text) t.innerHTML = text;
    });
  }
  // registered after theme.js's own listener, so it runs after PromoBars.refresh and has the last word
  document.addEventListener('cart:updated', (e) => refreshMilestones(e.detail.cart));

  /* ---------- 2. Bonus rules on the cart total ---------- */
  const bonus = SG.bonus;
  if (bonus && typeof bonus.plan === 'function') {
    const basePlan = bonus.plan;
    bonus.plan = function (cart) {
      const saved = this.rules;
      const totalRules = saved.filter((r) => r.trigger === 'total');
      let out;
      this.rules = saved.filter((r) => r.trigger !== 'total');
      try { out = basePlan.call(this, cart); } finally { this.rules = saved; }
      if (!totalRules.length) return out;
      // paid lines only: free rewards (mystery pins, the pack itself) never count toward the total
      const paid = cart.items.reduce((n, it) => (isBonus(it) ? n : n + (Number(it.final_line_price) || 0)), 0);
      totalRules.forEach((rule) => {
        const vid = Number(rule.reward_variant_id);
        const min = Math.round((Number(rule.min_total) || 0) * 100);
        const reqOk = !rule.requires_type || typeCount(cart, rule.requires_type) > 0;
        const won = min > 0 && paid >= min && reqOk ? 1 : 0;
        const target = won * (Number(rule.reward_quantity) || 1);
        const lines = cart.items.filter((it) => it.variant_id === vid && isBonus(it));
        const current = lines.reduce((n, it) => n + it.quantity, 0);
        const key = this.guardKey(vid, cart);
        let stored = this.readGuard(key);
        if (won < stored) { stored = won; this.writeGuard(key, won); } // dropped below: arm again for the next climb
        if (current < target) {
          if (stored === won || this.failed.has(vid)) return; // removed on purpose, or the add failed before
          out.adds.push({ id: vid, quantity: target - current, properties: { _bonus: '1' }, message: rule.message, key, won });
        } else if (current > target) {
          let remaining = target;
          lines.forEach((it) => { const q = Math.min(remaining, it.quantity); if (q !== it.quantity) out.changes.push({ id: it.key, quantity: q }); remaining -= q; });
          this.writeGuard(key, won);
        }
      });
      return out;
    };

    const baseApply = bonus.apply;
    bonus.apply = async function (cart) {
      await baseApply.call(this, cart);
      try { await verifyTotalRewards(this); } catch (e) { warn('verify', e); }
    };
    async function verifyTotalRewards(b) {
      const totalRules = b.rules.filter((r) => r.trigger === 'total');
      const cart = SG.cart.state;
      if (!totalRules.length || !cart || b.running) return;
      for (const rule of totalRules) {
        const vid = Number(rule.reward_variant_id);
        const paidLines = cart.items.filter((it) => it.variant_id === vid && isBonus(it) && Number(it.final_line_price) > 0);
        if (!paidLines.length) continue;
        // The reward sits in the cart at a price: the discount that makes it free is missing or inactive.
        // Take it out rather than charge for a "free" item, and don't retry on this page view.
        b.failed.add(vid);
        b.writeGuard(b.guardKey(vid, cart), 1);
        for (const it of paidLines) {
          await SG.cart.fetchJson(SG.routes.cartChange + '.js', { method: 'POST', body: JSON.stringify({ id: it.key, quantity: 0 }) });
        }
        warn('reward was not free in the cart, removed it again', rule);
        await SG.cart.refresh();
      }
    }
  }

  /* ---------- 3. Drawer add-on (club) ---------- */
  /* Returns true when the code is on the cart and applicable (the line shows $0 in the drawer). False when the
     cart rejected it — e.g. once-per-customer and this customer already used it, or a code that can't combine
     is already applied — in which case it is stored for checkout, where the same rules decide. */
  async function applyCode(code) {
    if (!code) return false;
    try {
      const existing = ((SG.cart.state && SG.cart.state.discount_codes) || []).map((d) => d && d.code).filter((c) => c && c.toUpperCase() !== code.toUpperCase());
      const data = await SG.cart.fetchJson(SG.routes.cartUpdate + '.js', { method: 'POST', body: JSON.stringify({ discount: existing.concat(code).join(',') }) });
      const ok = (data.discount_codes || []).some((d) => d && String(d.code).toUpperCase() === code.toUpperCase() && d.applicable !== false);
      if (!ok) throw new Error('code not applied through the cart');
      await SG.cart.refresh();
      return true;
    } catch (e) {
      warn('discount via cart', e);
      // The /discount/CODE route stores the code for checkout instead.
      try {
        const root = (SG.routes.root || '/').replace(/\/$/, '');
        await fetch(root + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(SG.routes.cart || '/cart'), { credentials: 'same-origin' });
      } catch (err) { warn('discount route', err); }
      return false;
    }
  }
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-addon]');
    if (!btn || !SG.addToCart) return;
    e.preventDefault();
    const item = { id: Number(btn.dataset.variantId), quantity: 1, properties: { _source: 'cart-drawer' } };
    if (/^\d+$/.test(btn.dataset.sellingPlanId || '')) item.selling_plan = Number(btn.dataset.sellingPlanId);
    try {
      await SG.addToCart([item], btn, { source: 'drawer' });
      const applied = await applyCode(btn.dataset.code);
      // "first month free" is only promised when the cart actually shows it
      if (SG.toast) SG.toast(applied && btn.dataset.added ? btn.dataset.added : ((SG.strings && SG.strings.added) || 'Added'));
    } catch (err) { warn('addon', err); }
  });

  /* ---------- 3b. "pairs with" row in the drawer: the whole card is the add button ---------- */
  // Cards come from the product-recommendations section (image + title links, "Add +" for single-variant
  // products). In a phone-width drawer a tap on the picture must not leave the cart, so any tap on a card
  // with a quick-add button adds that product; theme.js handles the button click itself.
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.cart-drawer__recs .recommendations__card');
    if (!card || e.target.closest('[data-quick-add]')) return;
    const btn = $('[data-quick-add]', card);
    if (!btn) return; // multi-variant / subscription product: nothing to one-tap, the link stands
    e.preventDefault();
    btn.click();
  });

  /* ---------- 4. Express checkout buttons survive the re-render ---------- */
  const drawer = SG.drawer;
  if (typeof drawer.render === 'function') {
    const baseRender = drawer.render;
    drawer.render = function (html) {
      const keep = this.el && $('[data-cart-wallets]', this.el);
      baseRender.call(this, html);
      if (!this.el) return;
      // the subscription flag (shows/hides Shopify's recurring-purchase note) follows the fresh render
      try {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
        if (fresh && fresh.dataset.hasSubscription !== undefined) this.el.dataset.hasSubscription = fresh.dataset.hasSubscription;
      } catch (e) { warn('render', e); }
      if (!keep) return;
      const slot = $('[data-cart-wallets]', this.el);
      if (slot && slot !== keep) {
        keep.hidden = slot.hidden; // empty cart: nothing to pay for
        slot.replaceWith(keep);
      }
    };
  }
})();
